import { collectionData } from 'rxfire/firestore';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concat,
  exhaustMap,
  filter,
  firstValueFrom,
  map,
  merge,
  NEVER,
  of,
  ReplaySubject,
  scan,
  share,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  timeout,
  timer,
} from 'rxjs';
import { fromFetch } from 'rxjs/fetch';

import {
  CreateParticipantSchema,
  ParticipantsStatsSchema,
  ReorderParticipantsSchema,
  UpdateParticipantSchema,
} from '@pin/schemas/admin';
import type { ParticipantsStatsRow } from '@pin/schemas/database';
import { ErrorResponseSchema } from '@pin/schemas/shared';
import * as z from 'zod';
import { withIdToken } from '../firebase/auth';
import { participantsCol } from '../firebase/firestore/collections';
import { adminApiUrl } from '../utils/context';
import { isVisible$ } from '../utils/rxjs.dom';
import { HttpError, retryWithBackoff } from '../utils/rxjs.http';

export const participants$ = collectionData(participantsCol).pipe(
  map((p) => p?.toSorted((a, b) => a.maskedOrderIdx - b.maskedOrderIdx)),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const createParticipant = (
  data: z.input<typeof CreateParticipantSchema>
): Promise<void> =>
  firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${adminApiUrl}/api/participants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(CreateParticipantSchema.parse(data)),
        selector: async (res) => {
          if (!res.ok) {
            throw new HttpError(res);
          }
        },
      }).pipe(timeout(10_000))
    )
  );

export const updateParticipant = (
  id: string,
  data: z.input<typeof UpdateParticipantSchema>
): Promise<void> =>
  firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${adminApiUrl}/api/participants/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(UpdateParticipantSchema.parse(data)),
        selector: async (res) => {
          if ([404].includes(res.status)) {
            throw new Error(
              ErrorResponseSchema.parse(await res.json()).message
            );
          }
          if (!res.ok) {
            throw new HttpError(res);
          }
        },
      }).pipe(timeout(10_000))
    )
  );

export const reorderParticipants = (
  data: z.input<typeof ReorderParticipantsSchema>
): Promise<void> =>
  firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${adminApiUrl}/api/participants/order`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(ReorderParticipantsSchema.parse(data)),
        selector: async (res) => {
          if ([404].includes(res.status)) {
            throw new Error(
              ErrorResponseSchema.parse(await res.json()).message
            );
          }
          if (!res.ok) {
            throw new HttpError(res);
          }
        },
      }).pipe(timeout(10_000))
    )
  );

export const deleteParticipant = (id: string): Promise<void> =>
  firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${adminApiUrl}/api/participants/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
        selector: async (res) => {
          if ([404].includes(res.status)) {
            throw new Error(
              ErrorResponseSchema.parse(await res.json()).message
            );
          }
          if (!res.ok) throw new HttpError(res);
        },
      }).pipe(timeout(10_000))
    )
  );

const STATS_POLL_INTERVAL_MS = 20_000;
const STATS_REFRESH_COOLDOWN_MS = 5_000;
let lastStatsRefreshAt = 0;
const STATS_TIMEOUT_MS = 10_000;
const STATS_RETRY_COUNT = 3;
/* After data arrives, the UI shows a "fresh" indicator for this duration so
 * users see that a refresh happened, even when the fetch was instantaneous. */
const STATS_FRESH_MS = 1_000;

export type ParticipantsStats = Map<string, ParticipantsStatsRow>;
export interface ParticipantsStatsState {
  data: ParticipantsStats | null;
  loading: boolean;
  failed: boolean;
  /** Timestamp until which the data should be considered "just refreshed". */
  freshUntil: number;
}

const STATS_INITIAL_STATE: ParticipantsStatsState = {
  data: null,
  loading: true,
  failed: false,
  freshUntil: 0,
};

const statsRequest$ = concat(
  of({ loading: true } as const),
  withIdToken((token) =>
    fromFetch(`${adminApiUrl}/api/participants/stats`, {
      headers: { Authorization: `Bearer ${token}` },
      selector: async (res) => {
        if (!res.ok) throw new HttpError(res);
        const { stats } = ParticipantsStatsSchema.parse(await res.json());
        return new Map(
          stats.map((row) => [row.participantId, row])
        ) as ParticipantsStats;
      },
    }).pipe(timeout(STATS_TIMEOUT_MS))
  ).pipe(
    retryWithBackoff(STATS_RETRY_COUNT),
    map((data) => ({
      data,
      failed: false,
      freshUntil: Date.now() + STATS_FRESH_MS,
    })),
    catchError((e: unknown) => {
      console.error(e);
      return of({ data: null, failed: true, freshUntil: 0 });
    })
  )
);

/** Manual "hent nå" requests, merged into the poll. */
const statsRefresh$ = new Subject<void>();
export const refreshParticipantsStats = () => statsRefresh$.next();

/** Auto-refresh on/off. Owned here so the poll survives a component remount. */
const statsAutoRefresh$ = new BehaviorSubject(true);
export const participantsStatsAutoRefresh$ = statsAutoRefresh$.asObservable();
export const setParticipantsStatsAutoRefresh = (enabled: boolean) =>
  statsAutoRefresh$.next(enabled);

/**
 * Favorite/guess counts, polled every 10s while the tab is visible and
 * auto-refresh is on. Hiding the tab tears the timer down; showing it again
 * fetches straight away, so the numbers are never stale on return.
 */
export const participantsStats$ = combineLatest([
  isVisible$,
  statsAutoRefresh$,
]).pipe(
  switchMap(([visible, autoRefresh]) => {
    if (!visible) {
      return NEVER;
    }

    return merge(
      autoRefresh ? timer(0, STATS_POLL_INTERVAL_MS) : NEVER,
      statsRefresh$
    ).pipe(
      filter(
        () => Date.now() - lastStatsRefreshAt >= STATS_REFRESH_COOLDOWN_MS
      ),
      exhaustMap(() => {
        lastStatsRefreshAt = Date.now();
        return statsRequest$;
      })
    );
  }),
  scan(
    (state: ParticipantsStatsState, next) =>
      'loading' in next
        ? { ...state, loading: true }
        : /* A failed poll keeps the last good numbers on screen rather than
           * blanking the grid. */
          {
            data: next.data ?? state.data,
            failed: next.failed,
            freshUntil: next.freshUntil,
            loading: false,
          },
    STATS_INITIAL_STATE
  ),
  startWith(STATS_INITIAL_STATE),
  // Keep the last numbers through page navigation during the refresh cooldown.
  share({
    connector: () => new ReplaySubject<ParticipantsStatsState>(1),
    resetOnRefCountZero: () => timer(STATS_REFRESH_COOLDOWN_MS),
  })
);
